import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { useNavigate, useMatch } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";

// @mui material components
import { Grid, Card, Box, Tab, Tabs } from "@mui/material";
import { tabsClasses } from "@mui/material/Tabs";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// Local & Custom Components
import Map from "components/Maps/Map";
import NextEvent from "modules/clearways/views/NextEvent";
import CameraLatest from "components/Camera/CameraLatest";
import RevisedTable from "components/Tables/RevisedTable";

// APIs & Utilities
import groupAPI from "services/api/groups";
import bksAPI from "services/api/bks";
import clearwaysAPI from "modules/clearways/api";
import { processGroupData } from "utils/group";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { wrapper, loadingScreen } from "utils/wrapper";
import { setupGroupTabSelectors } from "utils/setup";
import { createDataTableRows } from "utils/device";
import { getNamingConventions } from "utils/formatting";

const MAPBOX_SETTINGS = {
    style: "mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true",
    zoom: 17,
    interactive: true,
};

function CustomTabPanel({ showTabs, children, value, index, ...other }) {
    if (value !== index) return null;

    return (
        <div
            role="tabpanel"
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ height: "100%", width: "100%", overflowX: "hidden" }}
            {...other}
        >
            <Box sx={{ px: 1, py: showTabs ? 0 : 1, height: `calc(100% - ${showTabs ? 0 : '16px'})`, width: "calc(100% - 16px)" }}>
                {children}
            </Box>
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    value: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

function ClearwayDeviceGroupView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;
    const isMobile = deviceLayout === "mobile";

    const auth0 = useAuth0();
    const navigate = useNavigate();
    const match = useMatch("clearways/groups/:group_id");
    const group_id = match?.params?.group_id;

    const [tabValue, setTabValue] = useState(0);

    // Load API tokens safely
    useEffect(() => {
        groupAPI.loadToken(auth0);
        bksAPI.loadToken(auth0);
        clearwaysAPI.loadToken(auth0);
    }, [auth0]);

    // Handle map disabling
    useEffect(() => {
        if (map?.current) {
            disableMap(map, user, selectedOrg);
        }
    }, [map, user, selectedOrg]);

    // Date constants derived once
    const date = useMemo(() => moment(), []);
    const month = date.month();
    const year = date.year();

    const showAdvancedDeviceLevel = userPermissions.includes("devices-advanced:view");

    // React Query Calls
    const devices = useQuery({
        queryKey: ["getAllDevices", selectedOrg],
        queryFn: () => bksAPI.getAllDevices(),
    });

    const group = useQuery({
        queryKey: ["getGroupWithID", group_id, selectedOrg],
        queryFn: () => groupAPI.getGroupWithID(group_id),
        enabled: !!group_id,
    });

    const clearway = useQuery({
        queryKey: ["getClearwaysWithYearAndMonth", year, month, selectedOrg],
        queryFn: () => clearwaysAPI.getClearwaysWithYearAndMonth(year, month),
    });

    if (group.isLoading || devices.isLoading || clearway.isLoading) {
        return loadingScreen(controller, true);
    }

    if (group.error || devices.error || clearway.error) {
        return wrapper("Error!", controller);
    }

    if (!group.data || !devices.data || !clearway.data) {
        return loadingScreen(controller, false);
    }

    const groupData = processGroupData(group.dataUpdatedAt, group.data, devices.data, showAdvancedDeviceLevel);
    const dataTableData = createDataTableRows(groupData.devices, showAdvancedDeviceLevel);

    let polygonColour = "#58ba2d";
    if (!groupData.all_updated) {
        polygonColour = "#ff667c";
    } else if (groupData.devices_monitored > 0 && showAdvancedDeviceLevel) {
        polygonColour = "#edaa00";
    }

    // Calculate tabs using standard variables instead of useMemo to avoid Hook order violations
    const baseTabs = setupGroupTabSelectors(userPermissions, groupData);
    let tabSelectors = [...baseTabs];

    // Inject Map and Next Event into tabs if on mobile layout
    if (isMobile) {
        const overviewIdx = tabSelectors.indexOf("Overview");
        if (overviewIdx !== -1) {
            tabSelectors.splice(overviewIdx + 1, 0, "Map", "Next Event");
        } else {
            tabSelectors.push("Map", "Next Event");
        }
    }

    const showTabs = tabSelectors.length > 1;

    const activeCount = groupData.devices.filter((d) => d.active).length;
    const reservedCount = groupData.devices.filter((d) => !d.active).length;

    const [header, subheader, triheader] = getNamingConventions(group.data.name);

    const headerValues = (
        <SoftBox display="flex" pb={1} pt={0.5} flexDirection="row" justifyContent="space-between" alignItems="center">
            <SoftBox>
                <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" sx={{ mr: 0.5, flexShrink: 1, width: 'fit-content' }}>{header}</SoftTypography>
                {subheader && <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="medium" color="text" sx={{ minWidth: "fit-content" }}>{subheader}{triheader && ', '}</SoftTypography>}
                {triheader && <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="medium" color="text" sx={{ minWidth: "fit-content" }}>{triheader}</SoftTypography>}
            </SoftBox>
            <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="flex-end" gap={0.5}>
                <SoftTypography lineHeight={1.4} variant="h6" fontWeight="semi-bold" color="dark">
                    {`${activeCount} Active Device${activeCount !== 1 ? "s" : ""}`}
                </SoftTypography>
                {showAdvancedDeviceLevel && (
                    <SoftTypography lineHeight={1.4} variant="caption" fontWeight="regular" color="text">
                        {`${reservedCount} Reserved Device${reservedCount !== 1 ? "s" : ""}`}
                    </SoftTypography>
                )}
            </SoftBox>
        </SoftBox>
    );

    const handleRowClick = (row) => {
        if (row?.original?.uuid) {
            navigate(`../../devices/${row.original.uuid}`);
        }
    };

    const cameraDevices = group.data.devices.filter((d) => d.hardware_type === "bk-camera");

    // Reusable components for layout rendering
    const renderMapComponent = () => (
        <Card onClick={(e) => e.stopPropagation()} sx={{ minHeight: "200px", height: "100%", overflow: "hidden" }}>
            <Map
                selected_group={groupData}
                parent_group={groupData.parent_group}
                allow_devices={true}
                colour={polygonColour}
                settings={MAPBOX_SETTINGS}
                padding={30}
            />
        </Card>
    );

    const renderNextEventComponent = () => (
        <SoftBox height="100%" onClick={(e) => e.stopPropagation()}>
            <NextEvent data={clearway.data} group_name={groupData.name} groupID={group_id} />
        </SoftBox>
    );

    // Dynamic Index Helpers
    const getTabIndex = (name) => tabSelectors.indexOf(name);

    return wrapper(
        <SoftBox mb={isMobile ? 1 : 0} sx={({ palette, functions: { rgba } }) => ({ borderRadius: "20px", backgroundColor: rgba(palette[appearance].background, 0.7), overflow: "hidden", height: 'calc(100%)', maxWidth: "100%" })}>
            {showTabs && (
                <Grid container spacing={0} sx={{ width: "100%", margin: 0 }}>
                    <Grid item xxs={12}>
                        <SoftBox p={1}>
                            <Tabs
                                value={tabValue}
                                onChange={(e, val) => setTabValue(val)}
                                aria-label="GroupView Tabs"
                                allowScrollButtonsMobile
                                variant="scrollable"
                                scrollButtons
                                sx={{
                                    maxWidth: "100%",
                                    [`& .${tabsClasses.scrollButtons}`]: {
                                        "&.Mui-disabled": { opacity: 0 },
                                    },
                                    "& .MuiTabs-indicator": {
                                        top: "3px",
                                        height: "calc(100% - 6px)",
                                    },
                                }}
                            >
                                {tabSelectors.map((row, index) => (
                                    <Tab
                                        key={row}
                                        sx={{ minWidth: "fit-content !important", padding: "12px !important" }}
                                        label={row}
                                        {...a11yProps(index)}
                                    />
                                ))}
                            </Tabs>
                        </SoftBox>
                    </Grid>
                </Grid>
            )}

            {/* OVERVIEW TAB */}
            <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Overview")}>
                <SoftBox
                    borderRadius="20px"
                    color="white"
                    width="100%"
                    display="flex"
                    height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : isMobile ? "4.5rem" : "6.5rem"} - 16px)`}
                    minHeight={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : isMobile ? "4.5rem" : "6.5rem"} - 16px)`}
                    sx={({ palette, functions: { rgba } }) => ({ overflow: "hidden" })}
                >
                    <SoftBox display="flex" height="100%" width="100%" flex={1} gap={1} sx={{ overflow: "hidden" }}>
                        <SoftBox display="flex" flex={{ lg: 7, x3l: 8 }} flexDirection="column" height="100%" minHeight={0} minWidth={0}>
                            <RevisedTable
                                header={headerValues}
                                table={dataTableData}
                                showAdvancedDeviceLevel={showAdvancedDeviceLevel}
                                onRowClicked={handleRowClick}
                                canSearch
                            />
                        </SoftBox>

                        {/* Desktop Side Column with Map and Event (Hidden on Mobile) */}
                        {!isMobile && (
                            <SoftBox display="flex" flex={{ lg: 5, x3l: 4 }} flexDirection="column" height="100%" minWidth={0}>
                                <SoftBox sx={{ minHeight: "200px", height: "100%" }} pb={1}>
                                    {renderMapComponent()}
                                </SoftBox>
                                <SoftBox sx={{ height: "100%", minHeight: 0 }}>
                                    {renderNextEventComponent()}
                                </SoftBox>
                            </SoftBox>
                        )}
                    </SoftBox>
                </SoftBox>
            </CustomTabPanel>

            {/* MOBILE MAP TAB */}
            {isMobile && (
                <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Map")}>
                    <SoftBox height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : "4.5rem"} - 16px)`} width="100%">
                        {renderMapComponent()}
                    </SoftBox>
                </CustomTabPanel>
            )}

            {/* MOBILE NEXT EVENT TAB */}
            {isMobile && (
                <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Next Event")}>
                    <SoftBox height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : "4.5rem"} - 16px)`} width="100%">
                        {renderNextEventComponent()}
                    </SoftBox>
                </CustomTabPanel>
            )}

            {/* CAMERA FEEDS TAB */}
            <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Camera Feeds")}>
                <SoftBox height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : "4.5rem"} - 16px)`} width="100%" sx={{ overflowY: 'auto', background: '#eee', borderRadius: '12px' }}>
                    <Grid container>
                        {cameraDevices.map((row, index) => (
                            <Grid item xxs={12} sm={6} md={4} lg={3} xl={2} key={row.uuid || index}>
                                <SoftBox m={1}>
                                    <Card>
                                        <SoftBox p={1}>
                                            <SoftTypography pl={1} pt={1} variant="h6" fontWeight="regular" color="text" textAlign="center">
                                                eMinor {row.name}
                                            </SoftTypography>
                                            <CameraLatest uuid={row.uuid} no_padding={true} />
                                        </SoftBox>
                                    </Card>
                                </SoftBox>
                            </Grid>
                        ))}
                    </Grid>
                </SoftBox>
            </CustomTabPanel>

            {/* GROUP SETTINGS TAB */}
            <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Group Settings")}>
                <SoftTypography variant="h4">Group Settings</SoftTypography>
            </CustomTabPanel>
        </SoftBox>,
        controller
    );
}

ClearwayDeviceGroupView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
};

export default ClearwayDeviceGroupView;